import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, LogOut, Upload, Save, Package, Settings, Users, ExternalLink } from "lucide-react";
import { api, resolveImg, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const emptyProduct = { name: "", description: "", price: 0, category: "Suplemento", tags: [], image: "", rating: 4.5, featured: false, order: 0 };
const emptyTestimonial = { name: "Cliente Vida Divina", result: "", caption: "Antes y Después", image: "", order: 0 };

function ImageUpload({ value, onChange, testid }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al subir imagen");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <div className="h-20 w-20 rounded-xl bg-sand-100 overflow-hidden border border-forest-900/10 shrink-0">
        {value ? <img src={resolveImg(value)} alt="preview" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-forest-700/30 text-xs">Sin imagen</div>}
      </div>
      <div className="flex-1">
        <input ref={inputRef} type="file" accept="image/*" hidden onChange={handleFile} data-testid={testid} />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 rounded-lg bg-forest-900 text-sand px-4 py-2 text-sm font-medium hover:bg-terracotta-500 transition-colors disabled:opacity-60"
        >
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
          Subir imagen
        </button>
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="o pega una URL de imagen"
          className="mt-2 w-full rounded-lg border border-forest-900/15 px-3 py-2 text-xs outline-none focus:border-terracotta-500"
        />
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-lg border border-forest-900/15 px-3 py-2.5 text-sm outline-none focus:border-terracotta-500 transition-colors bg-white";
const labelCls = "text-xs font-semibold text-forest-800 uppercase tracking-wide";

function ProductForm({ initial, onSave, onCancel }) {
  const [p, setP] = useState({ ...emptyProduct, ...initial, tags: initial?.tags || [] });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!p.name) return toast.error("El nombre es obligatorio");
    setSaving(true);
    try {
      const payload = { ...p, price: parseFloat(p.price) || 0, rating: parseFloat(p.rating) || 4.5, order: parseInt(p.order) || 0,
        tags: Array.isArray(p.tags) ? p.tags : String(p.tags).split(",").map((t) => t.trim()).filter(Boolean) };
      if (initial?.id) await api.put(`/products/${initial.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Producto guardado");
      onSave();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 border border-forest-900/10 space-y-4" data-testid="product-form">
      <div className="grid md:grid-cols-2 gap-4">
        <div><label className={labelCls}>Nombre</label><input data-testid="pf-name" className={inputCls} value={p.name} onChange={(e) => set("name", e.target.value)} /></div>
        <div><label className={labelCls}>Categoría</label>
          <input data-testid="pf-category" className={inputCls} value={p.category} onChange={(e) => set("category", e.target.value)} placeholder="Té, Café, Suplemento" />
        </div>
      </div>
      <div><label className={labelCls}>Descripción</label><textarea data-testid="pf-desc" className={inputCls} rows={3} value={p.description} onChange={(e) => set("description", e.target.value)} /></div>
      <div className="grid md:grid-cols-3 gap-4">
        <div><label className={labelCls}>Precio ($)</label><input data-testid="pf-price" type="number" step="0.01" className={inputCls} value={p.price} onChange={(e) => set("price", e.target.value)} /></div>
        <div><label className={labelCls}>Rating</label><input type="number" step="0.1" min="0" max="5" className={inputCls} value={p.rating} onChange={(e) => set("rating", e.target.value)} /></div>
        <div><label className={labelCls}>Orden</label><input type="number" className={inputCls} value={p.order} onChange={(e) => set("order", e.target.value)} /></div>
      </div>
      <div><label className={labelCls}>Etiquetas (separadas por coma)</label><input className={inputCls} value={Array.isArray(p.tags) ? p.tags.join(", ") : p.tags} onChange={(e) => set("tags", e.target.value)} /></div>
      <div><label className={labelCls}>Imagen</label><div className="mt-1.5"><ImageUpload value={p.image} onChange={(v) => set("image", v)} testid="pf-image" /></div></div>
      <div className="flex gap-3 pt-2">
        <button data-testid="pf-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-forest-900 text-sand px-5 py-2.5 text-sm font-semibold hover:bg-terracotta-500 transition-colors disabled:opacity-60">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Guardar
        </button>
        <button onClick={onCancel} className="rounded-lg border border-forest-900/15 px-5 py-2.5 text-sm font-medium hover:bg-sand-100">Cancelar</button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("productos");
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [content, setContent] = useState(null);
  const [editing, setEditing] = useState(null); // product being edited or "new"
  const [savingContent, setSavingContent] = useState(false);
  const [newT, setNewT] = useState(null);

  useEffect(() => {
    if (user === false) navigate("/admin");
  }, [user, navigate]);

  const load = () => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/content").then((r) => setContent(r.data));
  };
  useEffect(() => { load(); }, []);

  const delProduct = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Producto eliminado");
    load();
  };

  const saveContent = async () => {
    setSavingContent(true);
    try {
      await api.put("/content", content);
      toast.success("Información actualizada");
    } catch (err) {
      toast.error("Error al guardar");
    } finally {
      setSavingContent(false);
    }
  };

  const saveTestimonial = async () => {
    try {
      await api.post("/testimonials", newT);
      toast.success("Testimonio agregado");
      setNewT(null);
      load();
    } catch (err) { toast.error("Error al guardar"); }
  };
  const delTestimonial = async (id) => {
    if (!window.confirm("¿Eliminar testimonio?")) return;
    await api.delete(`/testimonials/${id}`);
    load();
  };

  const setC = (k, v) => setContent((s) => ({ ...s, [k]: v }));

  if (user === null || !content) {
    return <div className="min-h-screen flex items-center justify-center bg-sand"><Loader2 className="animate-spin text-forest-900" /></div>;
  }

  const tabs = [
    { id: "productos", label: "Productos", icon: Package },
    { id: "info", label: "Información", icon: Settings },
    { id: "testimonios", label: "Testimonios", icon: Users },
  ];

  return (
    <div className="min-h-screen bg-sand-100">
      {/* Top bar */}
      <div className="bg-forest-900 text-sand">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <div className="font-serif text-xl">Panel de Administración</div>
            <div className="text-sand/60 text-xs">{user.email}</div>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-sand/80 hover:text-sand"><ExternalLink size={15} /> Ver sitio</a>
            <button data-testid="logout-btn" onClick={() => { logout(); navigate("/admin"); }} className="inline-flex items-center gap-2 rounded-lg bg-sand/10 px-4 py-2 text-sm hover:bg-terracotta-500 transition-colors"><LogOut size={15} /> Salir</button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-forest-900/10">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => { setTab(t.id); setEditing(null); }}
              className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t.id ? "border-terracotta-500 text-forest-900" : "border-transparent text-forest-700/50 hover:text-forest-900"}`}
            >
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {/* PRODUCTS */}
        {tab === "productos" && (
          <div className="space-y-6">
            {editing ? (
              <ProductForm initial={editing === "new" ? null : editing} onSave={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
            ) : (
              <button data-testid="add-product-btn" onClick={() => setEditing("new")} className="inline-flex items-center gap-2 rounded-lg bg-forest-900 text-sand px-5 py-2.5 text-sm font-semibold hover:bg-terracotta-500 transition-colors">
                <Plus size={16} /> Nuevo producto
              </button>
            )}

            <div className="grid gap-3">
              {products.map((p) => (
                <div key={p.id} data-testid={`admin-product-${p.id}`} className="bg-white rounded-xl p-4 border border-forest-900/10 flex items-center gap-4">
                  <img src={resolveImg(p.image)} alt={p.name} className="h-16 w-16 rounded-lg object-cover bg-sand-100" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-forest-900 truncate">{p.name}</div>
                    <div className="text-xs text-forest-700/60">{p.category} · ${p.price}</div>
                  </div>
                  <button onClick={() => setEditing(p)} className="rounded-lg border border-forest-900/15 px-3 py-2 text-sm hover:bg-sand-100">Editar</button>
                  <button data-testid={`del-product-${p.id}`} onClick={() => delProduct(p.id)} className="rounded-lg bg-destructive/10 text-destructive p-2 hover:bg-destructive hover:text-white transition-colors"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CONTENT */}
        {tab === "info" && (
          <div className="bg-white rounded-2xl p-6 md:p-8 border border-forest-900/10 space-y-5 max-w-3xl">
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className={labelCls}>Nombre del negocio</label><input data-testid="c-brand" className={inputCls} value={content.brand_name} onChange={(e) => setC("brand_name", e.target.value)} /></div>
              <div><label className={labelCls}>Badge / Etiqueta</label><input className={inputCls} value={content.hero_badge} onChange={(e) => setC("hero_badge", e.target.value)} /></div>
            </div>
            <div><label className={labelCls}>Título principal (Hero)</label><input data-testid="c-hero-title" className={inputCls} value={content.hero_title} onChange={(e) => setC("hero_title", e.target.value)} /></div>
            <div><label className={labelCls}>Subtítulo (Hero)</label><textarea className={inputCls} rows={3} value={content.hero_subtitle} onChange={(e) => setC("hero_subtitle", e.target.value)} /></div>
            <div><label className={labelCls}>Logo del negocio</label><div className="mt-1.5"><ImageUpload value={content.logo_url} onChange={(v) => setC("logo_url", v)} testid="c-logo" /></div></div>
            <div><label className={labelCls}>Imagen del Hero (fondo)</label><div className="mt-1.5"><ImageUpload value={content.hero_image} onChange={(v) => setC("hero_image", v)} testid="c-hero-img" /></div></div>
            <div><label className={labelCls}>Imagen "Nosotros"</label><div className="mt-1.5"><ImageUpload value={content.about_image} onChange={(v) => setC("about_image", v)} /></div></div>
            <div><label className={labelCls}>Título "Nosotros"</label><input className={inputCls} value={content.about_title} onChange={(e) => setC("about_title", e.target.value)} /></div>
            <div><label className={labelCls}>Texto "Nosotros"</label><textarea className={inputCls} rows={4} value={content.about_text} onChange={(e) => setC("about_text", e.target.value)} /></div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className={labelCls}>Número WhatsApp (con código país, sin +)</label><input data-testid="c-whatsapp" className={inputCls} value={content.whatsapp_number} onChange={(e) => setC("whatsapp_number", e.target.value)} placeholder="19297598940" /></div>
              <div><label className={labelCls}>Correo de contacto</label><input className={inputCls} value={content.email} onChange={(e) => setC("email", e.target.value)} /></div>
            </div>
            <div><label className={labelCls}>Mensaje predeterminado de WhatsApp</label><input className={inputCls} value={content.whatsapp_message} onChange={(e) => setC("whatsapp_message", e.target.value)} /></div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className={labelCls}>Instagram (URL)</label><input className={inputCls} value={content.instagram_url} onChange={(e) => setC("instagram_url", e.target.value)} /></div>
              <div><label className={labelCls}>Facebook (URL)</label><input className={inputCls} value={content.facebook_url} onChange={(e) => setC("facebook_url", e.target.value)} /></div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div><label className={labelCls}>Stat: Países</label><input className={inputCls} value={content.stat_countries} onChange={(e) => setC("stat_countries", e.target.value)} /></div>
              <div><label className={labelCls}>Stat: Productos</label><input className={inputCls} value={content.stat_products} onChange={(e) => setC("stat_products", e.target.value)} /></div>
              <div><label className={labelCls}>Stat: Garantía</label><input className={inputCls} value={content.stat_guarantee} onChange={(e) => setC("stat_guarantee", e.target.value)} /></div>
            </div>
            <button data-testid="save-content-btn" onClick={saveContent} disabled={savingContent} className="inline-flex items-center gap-2 rounded-lg bg-forest-900 text-sand px-6 py-3 text-sm font-semibold hover:bg-terracotta-500 transition-colors disabled:opacity-60">
              {savingContent ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Guardar cambios
            </button>
          </div>
        )}

        {/* TESTIMONIALS */}
        {tab === "testimonios" && (
          <div className="space-y-6">
            {newT ? (
              <div className="bg-white rounded-2xl p-6 border border-forest-900/10 space-y-4 max-w-2xl">
                <div className="grid md:grid-cols-2 gap-4">
                  <div><label className={labelCls}>Nombre</label><input className={inputCls} value={newT.name} onChange={(e) => setNewT({ ...newT, name: e.target.value })} /></div>
                  <div><label className={labelCls}>Resultado</label><input className={inputCls} value={newT.result} onChange={(e) => setNewT({ ...newT, result: e.target.value })} placeholder="-12 lbs en 14 días" /></div>
                </div>
                <div><label className={labelCls}>Etiqueta</label><input className={inputCls} value={newT.caption} onChange={(e) => setNewT({ ...newT, caption: e.target.value })} /></div>
                <div><label className={labelCls}>Imagen (antes/después)</label><div className="mt-1.5"><ImageUpload value={newT.image} onChange={(v) => setNewT({ ...newT, image: v })} testid="t-image" /></div></div>
                <div className="flex gap-3">
                  <button data-testid="save-testimonial-btn" onClick={saveTestimonial} className="inline-flex items-center gap-2 rounded-lg bg-forest-900 text-sand px-5 py-2.5 text-sm font-semibold hover:bg-terracotta-500"><Save size={15} /> Guardar</button>
                  <button onClick={() => setNewT(null)} className="rounded-lg border border-forest-900/15 px-5 py-2.5 text-sm">Cancelar</button>
                </div>
              </div>
            ) : (
              <button data-testid="add-testimonial-btn" onClick={() => setNewT({ ...emptyTestimonial })} className="inline-flex items-center gap-2 rounded-lg bg-forest-900 text-sand px-5 py-2.5 text-sm font-semibold hover:bg-terracotta-500"><Plus size={16} /> Nuevo testimonio</button>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {testimonials.map((t) => (
                <div key={t.id} className="relative rounded-xl overflow-hidden aspect-[3/4] bg-forest-900 group">
                  <img src={resolveImg(t.image)} alt={t.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-forest-900/80 to-transparent" />
                  <div className="absolute bottom-2 left-2 right-2 text-sand text-xs"><div className="font-semibold">{t.name}</div><div className="opacity-80">{t.result}</div></div>
                  <button onClick={() => delTestimonial(t.id)} className="absolute top-2 right-2 rounded-lg bg-destructive text-white p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
                </div>
              ))}
              {testimonials.length === 0 && !newT && <p className="text-forest-700/50 text-sm col-span-full">Aún no hay testimonios. Agrega fotos de antes/después.</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
